/**
 * Setup-time card display data resolution for the Legendary Arena game
 * engine.
 *
 * Builds a Readonly<Record<CardExtId, UICardDisplay>> snapshot at
 * Game.setup() so UIState can surface card name / imageUrl / cost without
 * granting the client a runtime registry import. Sibling snapshot to
 * G.cardStats (WP-018), G.villainDeckCardTypes (WP-014B), and
 * G.cardKeywords (WP-025).
 *
 * Walks four card surfaces:
 *   - heroes      via registry.listCards() filtered to cardType === 'hero'
 *   - villains    via registry.getSet(setAbbr).villains[i].cards[j]
 *                 (FlatCard supplies name/imageUrl; SetData supplies vAttack)
 *   - henchmen    via registry.getSet(setAbbr).henchmen[i] (group-level)
 *                 — henchmen are NOT in FlatCard; flattenSet() omits them
 *   - mastermind  base card via registry.getSet(setAbbr).masterminds[i]
 *                 (FlatCard supplies name/imageUrl; SetData supplies vAttack)
 *
 * No @legendary-arena/registry import. No boardgame.io import. Setup-time
 * only. No randomness, no clocks, no I/O.
 */
import { parseCardStatValue } from '../economy/economy.logic.js';
// why: D-13702 fan-out — buildCardDisplayData must resolve hero card-instance
// copy counts identically to buildHeroDeckCards so G.cardDisplayData keys
// form a superset of the hero deck reservoir. The shared helper enforces
// byte-for-byte parity by construction; divergence between the three
// sites would cause silent display lookup misses for HQ slot rendering
// and hand display under specific RNG seeds. Per RS-4 lock, the helper
// is imported (not duplicated) from the canonical emitter site.
import { buildCardCountsNameLookup, resolveHeroCardCopyCount, } from './buildHeroDeck.js';
import { resolveEffectiveTwistCount } from './schemeSetupSizing.js';
// ---------------------------------------------------------------------------
// parseCostNullable — null-distinction wrapper around parseCardStatValue
// ---------------------------------------------------------------------------
// why: pre-flight 2026-04-29 PS-4 — registry data uses null/undefined to
// signal "no cost shown" (e.g., bystanders, scheme cards have no printed
// cost; villain/henchman vAttack is occasionally null on data-quality
// edge cases); parseCardStatValue returns 0 for both null and undefined,
// which conflates "no cost" with "free". This guard preserves the UX
// distinction. NOT a parallel parser — parseCardStatValue is still the
// only widener-handler for non-null values (star modifiers, plus
// modifiers, integers).
/**
 * Parses a registry cost / vAttack value into the UICardDisplay.cost
 * shape `number | null`.
 *
 * - `null` / `undefined` -> `null` (registry says "no cost shown")
 * - everything else -> `parseCardStatValue(value)` (handles "2*", "2+",
 *   integers, etc., returning a non-negative integer; never throws)
 *
 * @param value - Raw registry cost / vAttack value.
 * @returns Parsed cost as number, or null when the registry value is
 *   genuinely absent.
 */
function parseCostNullable(value) {
    if (value === null || value === undefined) {
        return null;
    }
    return parseCardStatValue(value);
}
// ---------------------------------------------------------------------------
// Runtime type guard (orchestration-side detection seam per D-10014)
// ---------------------------------------------------------------------------
/**
 * Runtime type guard for CardDisplayDataRegistryReader.
 *
 * Returns true if the registry object exposes the required methods
 * (listCards, getSet). Narrow test mocks that only implement
 * CardRegistryReader (listCards-only) will return false. Used by
 * buildInitialGameState as the orchestration-side diagnostic detection
 * seam (mirrors isVillainDeckRegistryReader / isMastermindRegistryReader
 * / isSchemeRegistryReader / isHeroAbilityRegistryReader per WP-113
 * D-10014).
 */
// why: D-10014 — orchestration-side diagnostic detection seam mirrors the
// four existing builder guards. Builder-internal `isXRegistryReader →
// empty` paths remain unchanged for defense-in-depth; this orchestration
// site is the primary detection seam.
export function isCardDisplayDataRegistryReader(registry) {
    if (!registry || typeof registry !== 'object')
        return false;
    const candidate = registry;
    return (typeof candidate.listCards === 'function' &&
        typeof candidate.getSet === 'function');
}
// ---------------------------------------------------------------------------
// parseQualifiedIdForSetup — local duplicate (WP-113 §6 precedent)
// ---------------------------------------------------------------------------
// why: WP-113 §6 step 1 — `import or duplicate locally — author choice`.
// Duplicating locally avoids a circular import between builders and
// matchSetup.validate.ts (per D-10014). Identical rejection rules to
// the validator's `parseQualifiedId`.
/**
 * Parses a set-qualified ID `<setAbbr>/<slug>` into its components.
 *
 * Returns null on any malformed input.
 */
function parseQualifiedIdForSetup(input) {
    if (typeof input !== 'string' || input.length === 0)
        return null;
    if (input !== input.trim())
        return null;
    const slashIndex = input.indexOf('/');
    if (slashIndex === -1)
        return null;
    if (input.indexOf('/', slashIndex + 1) !== -1)
        return null;
    const setAbbr = input.slice(0, slashIndex);
    const slug = input.slice(slashIndex + 1);
    if (setAbbr.length === 0 || slug.length === 0)
        return null;
    return { setAbbr, slug };
}
// ---------------------------------------------------------------------------
// buildCardDisplayData — sibling-snapshot builder
// ---------------------------------------------------------------------------
// why: sibling-snapshot pattern (sibling to G.cardStats per WP-018,
// G.villainDeckCardTypes per WP-014B, G.cardKeywords per WP-025) —
// resolve registry data at setup so moves and projections never query the
// registry at runtime. Returns Readonly<...> as a documentational signal
// at the type boundary (TypeScript does not enforce runtime immutability
// for Record values, but the type signal makes accidental writes
// grep-able in review).
/**
 * Builds a card display data lookup from registry data at setup time.
 *
 * Iterates heroes, villains, henchmen, the mastermind base card, the
 * generic Master Strikes, the scheme-twist virtual cards, and the
 * villain-deck bystander virtual cards reachable from the match config
 * and projects their display fields (name, imageUrl, cost) into
 * UICardDisplay records keyed by CardExtId.
 *
 * @param registry - Setup-time registry reader. Accepts unknown to
 *   support narrow test mocks (CardRegistryReader). When the registry
 *   does not satisfy CardDisplayDataRegistryReader structurally
 *   (listCards / getSet missing), returns an empty record gracefully —
 *   moves and projections handle missing entries via the
 *   UNKNOWN_DISPLAY_PLACEHOLDER fallback in uiState.build.ts.
 * @param matchConfig - Match setup configuration with selected entity IDs.
 * @param numPlayers - Number of players in the match. Read by section 7
 *   as the bystander-count fallback when the scheme does not carry
 *   `villainDeckBystanderCount`, and by the scheme-twist section for a
 *   per-player-count twist stack (D-24733). DO NOT replace `numPlayers`
 *   usage with `matchConfig.bystandersCount`; these represent different
 *   domains:
 *
 *   - `numPlayers` → virtual villain-deck bystanders (D-1412, setup-time
 *     composition); section 7 reads this.
 *   - `matchConfig.bystandersCount` → shared rescue-pile supply in
 *     `G.sharedPiles.bystanders`.
 *
 *   Conflating them will silently break villain-deck composition
 *   correctness (both are `number`; no type error). Mirrors
 *   `context.ctx.numPlayers` read in `villainDeck.setup.ts:262`.
 *   `MatchSetupConfig` does not carry `numPlayers` (9-field composition
 *   lock per `.claude/rules/code-style.md`), so `Game.setup()` passes
 *   `ctx.numPlayers` through here.
 * @returns Readonly record keyed by CardExtId with display payloads.
 */
// why: WP-172 — `numPlayers` is the third parameter (not the whole
// `SetupContext`) so the builder keeps its pure-helper posture intact
// (no `boardgame.io` import, no `ctx.random` exposure). See JSDoc
// `@param numPlayers` above for the load-bearing two-domain distinction
// from `matchConfig.bystandersCount`.
export function buildCardDisplayData(registry, matchConfig, numPlayers) {
    // why: layer-boundary precedent — narrow test mocks may only implement
    // a subset of the registry interface. Mirror buildCardStats:170–172
    // empty-record fallback. The orchestration site
    // (buildInitialGameState.ts) emits a single deterministic diagnostic
    // when the guard fails so the empty result is still observable in
    // G.messages without per-card noise.
    if (!isCardDisplayDataRegistryReader(registry)) {
        return {};
    }
    const result = {};
    const allFlatCards = registry.listCards();
    const heroFlatCards = filterFlatCardsByType(allFlatCards, 'hero');
    // --- 1. Heroes (FlatCard supplies name / imageUrl / cost) ---
    for (const heroDeckId of matchConfig.heroDeckIds) {
        const parsed = parseQualifiedIdForSetup(heroDeckId);
        if (parsed === null)
            continue;
        const deckCards = filterHeroCardsByDeckSlug(heroFlatCards, parsed.setAbbr, parsed.slug);
        for (const heroFlatCard of deckCards) {
            const extId = heroFlatCard.key;
            result[extId] = {
                extId,
                name: heroFlatCard.name,
                imageUrl: heroFlatCard.imageUrl,
                cost: parseCostNullable(heroFlatCard.cost),
            };
        }
    }
    // --- 1b. Hero card instances (WP-135 / WP-137 — slash-format ext_id with #<copyIndex>) ---
    // why: D-14102 — the per-copy fan-out now iterates physicalCards[].count
    // instead of summing per-side cardCounts via resolveHeroCardCopyCount.
    // Each physicalCard carries count, imageUrl, and ordered sides[];
    // sides[0] is the canonical face slug per D-14101. Card data (name,
    // cost) is resolved from heroEntry.cards by matching sides[0] to
    // card.slug. Falls back to the old per-card resolveHeroCardCopyCount
    // path when physicalCards is absent (defense-in-depth).
    for (const heroDeckId of matchConfig.heroDeckIds) {
        const parsed = parseQualifiedIdForSetup(heroDeckId);
        if (parsed === null)
            continue;
        const setData = registry.getSet(parsed.setAbbr);
        const heroEntry = findHeroEntryForDisplay(setData, parsed.slug);
        if (heroEntry === null)
            continue;
        const physicalCards = parseDisplayDataPhysicalCards(heroEntry.physicalCards);
        if (physicalCards.length > 0) {
            for (const physicalCard of physicalCards) {
                // why: WP-724 / D-24545 — emit a display entry for EACH face of the physical
                // card (sides[0] primary, and sides[1] when the card is split), so the picker
                // and the played-card projection can render the CHOSEN face's name / cost /
                // ability. The whole-card imageUrl (physicalCard.imageUrl shows both halves) is
                // reused for both faces. Solo cards have a single side and emit one face.
                for (const sideSlug of physicalCard.sides) {
                    const cardEntry = findCardEntryBySlug(heroEntry.cards, sideSlug);
                    const name = cardEntry !== null && typeof cardEntry.name === 'string' ? cardEntry.name : '';
                    const cost = cardEntry !== null ? parseCostNullable(cardEntry.cost ?? null) : null;
                    const abilityText = resolveHeroAbilityText(cardEntry);
                    const imageUrl = physicalCard.imageUrl;
                    const baseExtId = `${parsed.setAbbr}/${parsed.slug}/${sideSlug}`;
                    for (let copyIndex = 0; copyIndex < physicalCard.count; copyIndex++) {
                        const extId = `${baseExtId}#${copyIndex}`;
                        // why: per-copy fresh object literal — no aliasing across keys
                        // (WP-028 D-2802 aliasing prevention extended to setup-time
                        // sibling-snapshot fan-out).
                        const display = { extId, name, imageUrl, cost };
                        // why: WP-315 — omit abilityText when the card has no printed ability
                        // (never an empty string) so the optional-field contract holds.
                        if (abilityText !== undefined) {
                            display.abilityText = abilityText;
                        }
                        result[extId] = display;
                    }
                }
            }
        }
        else {
            // why: fallback to per-card resolveHeroCardCopyCount when
            // physicalCards is absent or empty — preserves backward
            // compatibility with data that has not been curated yet.
            const nameLookup = buildCardCountsNameLookup(heroEntry.cardCounts);
            for (const heroCardEntry of heroEntry.cards) {
                if (!heroCardEntry || typeof heroCardEntry !== 'object')
                    continue;
                if (typeof heroCardEntry.slug !== 'string')
                    continue;
                const rarityLabel = typeof heroCardEntry.rarityLabel === 'string' ? heroCardEntry.rarityLabel : '';
                const copyCount = resolveHeroCardCopyCount({ name: heroCardEntry.name, rarityLabel }, nameLookup);
                if (copyCount === null)
                    continue;
                const baseExtId = `${parsed.setAbbr}/${parsed.slug}/${heroCardEntry.slug}`;
                const name = typeof heroCardEntry.name === 'string' ? heroCardEntry.name : '';
                const imageUrl = typeof heroCardEntry.imageUrl === 'string' ? heroCardEntry.imageUrl : '';
                const cost = parseCostNullable(heroCardEntry.cost ?? null);
                const abilityText = resolveHeroAbilityText(heroCardEntry);
                for (let copyIndex = 0; copyIndex < copyCount; copyIndex++) {
                    const extId = `${baseExtId}#${copyIndex}`;
                    // why: per-copy fresh object literal — no aliasing across keys
                    const display = { extId, name, imageUrl, cost };
                    // why: WP-315 — omit abilityText when the card has no printed ability
                    // (never an empty string) so the optional-field contract holds.
                    if (abilityText !== undefined) {
                        display.abilityText = abilityText;
                    }
                    result[extId] = display;
                }
            }
        }
    }
    // --- 2. Villains (FlatCard supplies name/imageUrl; SetData vAttack) ---
    for (const villainGroupId of matchConfig.villainGroupIds) {
        const parsed = parseQualifiedIdForSetup(villainGroupId);
        if (parsed === null)
            continue;
        const villainCards = findVillainGroupCards(registry, parsed.setAbbr, parsed.slug);
        for (const villainCard of villainCards) {
            const matchingFlatCard = findFlatCardForVillain(allFlatCards, parsed.setAbbr, parsed.slug, villainCard.slug);
            if (matchingFlatCard === undefined)
                continue;
            const extId = matchingFlatCard.key;
            result[extId] = {
                extId,
                name: matchingFlatCard.name,
                imageUrl: matchingFlatCard.imageUrl,
                cost: parseCostNullable(villainCard.vAttack),
            };
            // why: WP-172 / D-16802 — villain copies are virtual-instanced;
            // each suffixed ext_id needs its own display entry so the UIState
            // projection's resolveDisplay does not fall through to
            // UNKNOWN_DISPLAY_PLACEHOLDER (the literal `<unknown>`) for
            // city-revealed villains. Mirrors the WP-135 hero card-instance
            // per-copy fan-out at section 1b and the WP-168 villain instancing
            // at `villainDeck.setup.ts:203`. Grammar byte-identity with that
            // emitter site is the regression-guard the §C cross-builder
            // superset test asserts. `copies` default is 1 — inlined verbatim
            // from `readVillainCopyCount` (`villainDeck.setup.ts:389–394`);
            // Rule §16.1 forbids helper extraction across two files / two
            // call sites.
            let copyCount = 1;
            if (typeof villainCard.copies === 'number' && villainCard.copies >= 1) {
                copyCount = villainCard.copies;
            }
            for (let copyIndex = 0; copyIndex < copyCount; copyIndex++) {
                const paddedIndex = String(copyIndex).padStart(2, '0');
                const copyExtId = `${parsed.setAbbr}-villain-${parsed.slug}-${villainCard.slug}-${paddedIndex}`;
                // why: per-copy fresh object literal — no aliasing across keys
                // (D-2802 / D-13502 / D-14102 precedent already in this file at
                // section 1b lines 383–388).
                result[copyExtId] = {
                    extId: copyExtId,
                    name: matchingFlatCard.name,
                    imageUrl: matchingFlatCard.imageUrl,
                    cost: parseCostNullable(villainCard.vAttack),
                };
            }
        }
    }
    // --- 3. Henchmen (group-level name/imageUrl/vAttack; 10 virtual copies) ---
    // why: henchmen are NOT in FlatCard — flattenSet() emits only
    // hero / mastermind / villain / scheme. Walk getSet().henchmen[*]
    // directly. All 10 virtual copies share the group-level fields. Ext_id
    // format `henchman-{groupSlug}-NN` (zero-padded 00..09) mirrors
    // buildCardStats:248–260 exactly so the join key matches G.cardStats.
    for (const henchmanGroupId of matchConfig.henchmanGroupIds) {
        const parsed = parseQualifiedIdForSetup(henchmanGroupId);
        if (parsed === null)
            continue;
        const henchmanGroup = findHenchmanGroup(registry, parsed.setAbbr, parsed.slug);
        if (henchmanGroup === null)
            continue;
        const groupName = henchmanGroup.name ?? '';
        const groupImageUrl = henchmanGroup.imageUrl ?? '';
        const groupCost = parseCostNullable(henchmanGroup.vAttack ?? null);
        for (let copyIndex = 0; copyIndex < 10; copyIndex++) {
            const paddedIndex = String(copyIndex).padStart(2, '0');
            const extId = `henchman-${henchmanGroup.slug}-${paddedIndex}`;
            result[extId] = {
                extId,
                name: groupName,
                imageUrl: groupImageUrl,
                cost: groupCost,
            };
        }
    }
    // --- 4. Mastermind cards: base + tactics (FlatCard name/imageUrl; SetData vAttack) ---
    // why: BOTH the base card AND every tactic card need a display entry. The
    // base card surfaces on the MastermindTile; each DEFEATED tactic is pushed
    // into the defeating player's victory pile by fightMastermind (a per-card
    // rendered zone), so a tactic with no display entry renders as the literal
    // `<unknown>`. Production symptom (match 1HU0E1bgAy7): a fully-defeated
    // 4-tactic mastermind left 4 `<unknown>` cards in the victory pile. Tactic
    // ext_ids share the base card's grammar —
    // `${setAbbr}-mastermind-${slug}-${card.slug}` (mastermind.setup.ts:240) —
    // which is exactly each mastermind card's FlatCard key (registry shared.ts
    // flattenSet), so the FlatCard supplies name/imageUrl and the SetData card
    // entry supplies vAttack for cost. Iterating ALL cards keeps the base-card
    // entry byte-identical to before and simply adds the tactic entries.
    const mastermindParsed = parseQualifiedIdForSetup(matchConfig.mastermindId);
    if (mastermindParsed !== null) {
        const mastermindCards = findMastermindCardsForDisplay(registry, mastermindParsed.setAbbr, mastermindParsed.slug);
        for (const mastermindCard of mastermindCards) {
            const cardKey = `${mastermindParsed.setAbbr}-mastermind-${mastermindParsed.slug}-${mastermindCard.slug}`;
            const matchingFlatCard = findFlatCardByKey(allFlatCards, cardKey);
            if (matchingFlatCard === undefined)
                continue;
            const extId = matchingFlatCard.key;
            result[extId] = {
                extId,
                name: matchingFlatCard.name,
                imageUrl: matchingFlatCard.imageUrl,
                cost: parseCostNullable(mastermindCard.vAttack ?? null),
            };
        }
    }
    // --- 5. Master Strikes (5 generic virtual cards per D-16801) ---
    // why: D-16801 — exactly 5 generic Master Strikes per villain deck.
    // The literal `5` is inlined verbatim from `villainDeck.setup.ts:130`
    // (MASTER_STRIKE_COUNT). RS-1 lock: tabletop rule invariant, not a
    // tuning knob; the two-file duplication mirrors the
    // HENCHMAN_COPIES_PER_GROUP precedent (literal `10` at section 3
    // line 474). Soft-skip on mastermind parse failure (mirrors the
    // section-4 mastermind soft-skip — only `Game.setup()` may throw).
    if (mastermindParsed !== null) {
        let strikeName = 'Master Strike';
        let strikeImageUrl = '';
        const mastermindSetData = registry.getSet(mastermindParsed.setAbbr);
        const tierOneStrike = findOtherEntryByCardType(mastermindSetData, 'mastermind-strike');
        if (tierOneStrike !== null) {
            strikeName = tierOneStrike.name;
            strikeImageUrl = tierOneStrike.imageUrl;
        }
        else {
            // why: D-17201 — empirically only 5 of 40 sets carry a
            // `mastermind-strike` entry in their per-set `other[]` (core,
            // msp1, vill, wtif, ssw1 as of 2026-05-23). The `core` set is the
            // canonical Marvel Legendary visual source for the generic Master
            // Strike; without this tier-2 cross-set fallback, 35/40 matches
            // would render the tier-3 broken-image placeholder.
            const coreSetData = registry.getSet('core');
            const tierTwoStrike = findOtherEntryByCardType(coreSetData, 'mastermind-strike');
            if (tierTwoStrike !== null) {
                strikeName = tierTwoStrike.name;
                strikeImageUrl = tierTwoStrike.imageUrl;
            }
        }
        for (let strikeIndex = 0; strikeIndex < 5; strikeIndex++) {
            const paddedIndex = String(strikeIndex).padStart(2, '0');
            const strikeExtId = `master-strike-${paddedIndex}`;
            // why: per-copy fresh object literal — no aliasing across keys
            // (D-2802 / D-13502 / D-14102 precedent). `cost: null` — Master
            // Strikes have no printed cost on the physical card.
            result[strikeExtId] = {
                extId: strikeExtId,
                name: strikeName,
                imageUrl: strikeImageUrl,
                cost: null,
            };
        }
    }
    // --- 6. Scheme Twists + 7. Villain-Deck Bystanders (shared scheme lookup) ---
    // why: D-10014 — Builder Filtering Order — iterate named set only.
    // Sections 6 + 7 share the scheme parse + `getSet(schemeSetAbbr)`
    // lookup; the WP §Scope-B section ordering groups by display-source
    // scope (mastermind-set → scheme-set) rather than mirroring
    // `villainDeck.setup.ts`'s 3-4-5 ordering. Soft-skip on parse /
    // lookup failure (mirrors section-4 mastermind soft-skip — only
    // `Game.setup()` may throw).
    const parsedScheme = parseQualifiedIdForSetup(matchConfig.schemeId);
    if (parsedScheme !== null) {
        const schemeSetData = registry.getSet(parsedScheme.setAbbr);
        const scheme = findSchemeInSetForDisplay(schemeSetData, parsedScheme.slug);
        // why: scheme card display entry — the flat card key for a scheme is
        // `{setAbbr}-scheme-{slug}` (no card-level slug). Resolves name and
        // imageUrl from the flat card list so UISchemeState.display renders
        // the scheme identity on the play surface.
        const schemeFlatCardKey = `${parsedScheme.setAbbr}-scheme-${parsedScheme.slug}`;
        const schemeFlatCard = findFlatCardByKey(allFlatCards, schemeFlatCardKey);
        if (schemeFlatCard !== undefined) {
            const schemeExtId = schemeFlatCard.key;
            result[schemeExtId] = {
                extId: schemeExtId,
                name: schemeFlatCard.name,
                imageUrl: schemeFlatCard.imageUrl,
                cost: null,
            };
        }
        if (scheme !== null) {
            // why: D-16702 — twist count from scheme metadata; fallback `8`
            // inlined verbatim from `villainDeck.setup.ts:124` SCHEME_TWIST_COUNT
            // (D-1411 default). Two-file literal duplication is the RS-1 / Rule
            // §16.1 pattern; do not extract a helper across files.
            let baseTwistCount = 8;
            if (typeof scheme.villainDeckTwistCount === 'number') {
                baseTwistCount = scheme.villainDeckTwistCount;
            }
            // why: D-24733 / D-24734 — the same per-player-count sizing buildVillainDeck applies,
            // so the display keys match the twist copies actually built.
            const twistCount = resolveEffectiveTwistCount(matchConfig.schemeId, matchConfig.mastermindId, numPlayers, baseTwistCount);
            let twistImageUrl = '';
            const tierOneTwist = findOtherEntryByCardType(schemeSetData, 'scheme-twist');
            if (tierOneTwist !== null) {
                twistImageUrl = tierOneTwist.imageUrl;
            }
            else {
                // why: D-17201 — empirically only 4 of 40 sets carry a
                // `scheme-twist` entry in their per-set `other[]` (core, msp1,
                // vill, wtif as of 2026-05-23). The `core` set is the canonical
                // Marvel Legendary visual source; tier-2 prevents 36/40 broken-
                // image tiles. The repeated `registry.getSet('core')` call (also
                // made in section 5) is negligible at setup time and keeps the
                // two sections independent of execution order.
                const coreSetData = registry.getSet('core');
                const tierTwoTwist = findOtherEntryByCardType(coreSetData, 'scheme-twist');
                if (tierTwoTwist !== null) {
                    twistImageUrl = tierTwoTwist.imageUrl;
                }
            }
            for (let twistIndex = 0; twistIndex < twistCount; twistIndex++) {
                const paddedIndex = String(twistIndex).padStart(2, '0');
                const twistExtId = `scheme-twist-${scheme.slug}-${paddedIndex}`;
                // why: per-copy fresh object literal — no aliasing across keys.
                // `name` is always the literal `'Scheme Twist'` (printed-card
                // name is fixed — `core.json:2570` `name: "Scheme Twist"`).
                result[twistExtId] = {
                    extId: twistExtId,
                    name: 'Scheme Twist',
                    imageUrl: twistImageUrl,
                    cost: null,
                };
            }
            // --- 7. Villain-Deck Bystanders ---
            // why: D-1412 — bystander count from scheme metadata
            // (`villainDeckBystanderCount`); fallback to `numPlayers` (the new
            // 3rd parameter — see function JSDoc for why this is NOT
            // `matchConfig.bystandersCount`). Mirrors the
            // `villainDeck.setup.ts:262` read of `context.ctx.numPlayers`.
            let bystanderCount = numPlayers;
            if (typeof scheme.villainDeckBystanderCount === 'number') {
                bystanderCount = scheme.villainDeckBystanderCount;
            }
            let bystanderName = 'Bystander';
            let bystanderImageUrl = '';
            // why: tier-1 slug-match MUST beat positional `bystanders[0]`
            // because msp1 / vill / wtif / wpnx carry the generic
            // `slug === 'bystander'` entry mixed with named characters at
            // non-zero array positions; reading `bystanders[0]` blindly would
            // silently mis-render those sets.
            const tierOneBystander = findGenericBystanderEntry(schemeSetData);
            if (tierOneBystander !== null) {
                bystanderName = tierOneBystander.name;
                bystanderImageUrl = tierOneBystander.imageUrl;
            }
            else {
                // why: tier-2 acknowledged-imperfect named-character fallback —
                // cvwr / ssw2 / xmen `bystanders[]` carry ONLY named characters
                // (no `slug === 'bystander'` entry), so `bystanders[0]` becomes
                // the displayed art (e.g. "Aspiring Hero" for cvwr) — least-bad
                // choice until upstream registry data backfills the generic
                // entry. NO `core`-set cross-set fallback for bystanders because
                // bystander identity is conceptually per-scheme; a generic
                // `core` bystander would be a misleading visual for a Civil War
                // or Secret Wars II match. dstr has `bystanders: []` and falls
                // through to tier-3 literal `{ name: 'Bystander', imageUrl: '' }`.
                const tierTwoBystander = findFirstBystanderEntry(schemeSetData);
                if (tierTwoBystander !== null) {
                    bystanderName = tierTwoBystander.name;
                    bystanderImageUrl = tierTwoBystander.imageUrl;
                }
            }
            for (let bystanderIndex = 0; bystanderIndex < bystanderCount; bystanderIndex++) {
                const paddedIndex = String(bystanderIndex).padStart(2, '0');
                const bystanderExtId = `bystander-villain-deck-${paddedIndex}`;
                // why: per-copy fresh object literal — no aliasing across keys.
                result[bystanderExtId] = {
                    extId: bystanderExtId,
                    name: bystanderName,
                    imageUrl: bystanderImageUrl,
                    cost: null,
                };
            }
        }
    }
    // --- 8. Well-Known Generic Cards (D-17301) ---
    // why: D-17301 — terminal augmentation pass — ensures well-known
    // ext_ids always resolve. Extends the WP-172 / D-17201 tiered display
    // resolution pattern to the six generic game-component ext_ids that
    // exist independent of any match composition (the four pile tokens
    // exported from `pilesInit.ts` + the two starting cards from
    // `buildInitialGameState.ts`). Production symptom (2026-05-23 match
    // `WT_9sGMLmdG`): the RevealOverlay popup surfaced `<unknown>` for
    // `pile-bystander` (captured-from-supply, ending in the victory pile)
    // and the hand row surfaced `<unknown>` for `starting-shield-trooper`.
    //
    // Section 8 is the LAST logical pass in this builder — it covers cards
    // that aren't part of any match-configuration-driven composition, so
    // it has no shared state with prior sections and must sit immediately
    // before `return result;` to preserve the "always-applied augmentation"
    // semantic. Re-ordering would silently change its role to "one of
    // many builder steps" which would invite mis-classification.
    //
    // why: the six ext_id literal strings below are INLINED rather than
    // imported from their source-of-truth (`pilesInit.ts:22/25/28/31` +
    // `buildInitialGameState.ts:74/77`). `buildInitialGameState.ts`
    // imports `buildCardDisplayData`, so a reverse value import would
    // form a true ESM circular import path — tolerated for value-only
    // cycles in lazy contexts but brittle and a layering smell. Drift
    // detection lives in `buildCardDisplayData.test.ts`: it imports the
    // six constants from the source modules (test file is a different
    // module — no cycle) and asserts `result[CONSTANT]` matches each
    // inlined literal below.
    const coreSetData = registry.getSet('core');
    // why: `ssw1` is the ONLY set carrying a `cardType === 'sidekick'`
    // entry in `other[]` as of 2026-05-23 (verified against
    // `data/cards/ssw1.json:2356-2362`). Single call site keeps Rule §16.1
    // (no premature abstraction). Future-proofing: if a later set
    // introduces a second sidekick entry, a separate WP must explicitly
    // broaden this lookup (e.g. a tiered cross-set fallback mirroring the
    // D-17201 pattern for Master Strike + Scheme Twist); silent widening
    // is forbidden.
    const ssw1SetData = registry.getSet('ssw1');
    // pile-bystander
    let wellKnownBystanderName = 'Bystander';
    let wellKnownBystanderImageUrl = '';
    const wellKnownBystanderEntry = findBystanderArrayEntry(coreSetData, 'bystander');
    if (wellKnownBystanderEntry !== null) {
        wellKnownBystanderName = wellKnownBystanderEntry.name;
        wellKnownBystanderImageUrl = wellKnownBystanderEntry.imageUrl;
    }
    result['pile-bystander'] = {
        extId: 'pile-bystander',
        name: wellKnownBystanderName,
        imageUrl: wellKnownBystanderImageUrl,
        cost: null,
    };
    // pile-wound
    let wellKnownWoundName = 'Wound';
    let wellKnownWoundImageUrl = '';
    const wellKnownWoundEntry = findWoundArrayEntry(coreSetData);
    if (wellKnownWoundEntry !== null) {
        wellKnownWoundName = wellKnownWoundEntry.name;
        wellKnownWoundImageUrl = wellKnownWoundEntry.imageUrl;
    }
    result['pile-wound'] = {
        extId: 'pile-wound',
        name: wellKnownWoundName,
        imageUrl: wellKnownWoundImageUrl,
        cost: null,
    };
    // pile-shield-officer
    // why: `cost: null` for SHIELD Officer — printed-cost surface only;
    // gameplay cost resolved elsewhere. `UICardDisplay.cost` is the
    // printed card cost, and the SHIELD Officer pile token has no printed
    // cost on its physical face. Officer's recruit-cost-3 lives in the
    // well-known `G.cardStats[SHIELD_OFFICER_EXT_ID]` entry assigned in
    // `buildInitialGameState.ts` beside the Agent/Trooper entries.
    let wellKnownOfficerName = 'S.H.I.E.L.D. Officer';
    let wellKnownOfficerImageUrl = '';
    const wellKnownOfficerEntry = findHeroByExactSlug(coreSetData, 'officer');
    if (wellKnownOfficerEntry !== null) {
        wellKnownOfficerName = wellKnownOfficerEntry.name;
        wellKnownOfficerImageUrl = wellKnownOfficerEntry.imageUrl;
    }
    result['pile-shield-officer'] = {
        extId: 'pile-shield-officer',
        name: wellKnownOfficerName,
        imageUrl: wellKnownOfficerImageUrl,
        cost: null,
    };
    // pile-sidekick
    let wellKnownSidekickName = 'Sidekick';
    let wellKnownSidekickImageUrl = '';
    const wellKnownSidekickEntry = findOtherEntryByCardType(ssw1SetData, 'sidekick');
    if (wellKnownSidekickEntry !== null) {
        wellKnownSidekickName = wellKnownSidekickEntry.name;
        wellKnownSidekickImageUrl = wellKnownSidekickEntry.imageUrl;
    }
    result['pile-sidekick'] = {
        extId: 'pile-sidekick',
        name: wellKnownSidekickName,
        imageUrl: wellKnownSidekickImageUrl,
        cost: null,
    };
    // starting-shield-agent
    let wellKnownAgentName = 'S.H.I.E.L.D. Agent';
    let wellKnownAgentImageUrl = '';
    const wellKnownAgentEntry = findHeroByExactSlug(coreSetData, 'agent');
    if (wellKnownAgentEntry !== null) {
        wellKnownAgentName = wellKnownAgentEntry.name;
        wellKnownAgentImageUrl = wellKnownAgentEntry.imageUrl;
    }
    result['starting-shield-agent'] = {
        extId: 'starting-shield-agent',
        name: wellKnownAgentName,
        imageUrl: wellKnownAgentImageUrl,
        cost: null,
    };
    // starting-shield-trooper
    let wellKnownTrooperName = 'S.H.I.E.L.D. Trooper';
    let wellKnownTrooperImageUrl = '';
    const wellKnownTrooperEntry = findHeroByExactSlug(coreSetData, 'trooper');
    if (wellKnownTrooperEntry !== null) {
        wellKnownTrooperName = wellKnownTrooperEntry.name;
        wellKnownTrooperImageUrl = wellKnownTrooperEntry.imageUrl;
    }
    result['starting-shield-trooper'] = {
        extId: 'starting-shield-trooper',
        name: wellKnownTrooperName,
        imageUrl: wellKnownTrooperImageUrl,
        cost: null,
    };
    return result;
}
// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
/**
 * Filters flat cards to only those with the given cardType.
 */
function filterFlatCardsByType(cards, cardType) {
    const result = [];
    for (const card of cards) {
        if (card.cardType === cardType) {
            result.push(card);
        }
    }
    return result;
}
/**
 * Filters hero flat cards by setAbbr first, then by hero deck slug.
 *
 * Hero FlatCard key format: {setAbbr}-hero-{heroSlug}-{slot}.
 * Mirrors filterHeroCardsByDeckSlug in economy.logic.ts.
 */
function filterHeroCardsByDeckSlug(heroCards, targetSetAbbr, heroDeckSlug) {
    const result = [];
    for (const card of heroCards) {
        if (card.setAbbr !== targetSetAbbr)
            continue;
        const heroSlug = extractHeroSlug(card);
        if (heroSlug === heroDeckSlug) {
            result.push(card);
        }
    }
    return result;
}
/**
 * Extracts the hero slug from a hero FlatCard.
 *
 * Key format: {setAbbr}-hero-{heroSlug}-{slot}. The heroSlug sits
 * between "hero-" and the final "-{slot}" segment.
 */
function extractHeroSlug(card) {
    const prefix = `${card.setAbbr}-hero-`;
    if (!card.key.startsWith(prefix))
        return '';
    const afterPrefix = card.key.slice(prefix.length);
    const lastDashIndex = afterPrefix.lastIndexOf('-');
    if (lastDashIndex === -1)
        return '';
    return afterPrefix.slice(0, lastDashIndex);
}
/**
 * Finds a hero entry within the named set's heroes[] by slug.
 *
 * Returns null if the named set is not loaded, malformed, or the hero
 * slug is not present. Used by the WP-135 hero card-instance walk for
 * G.cardDisplayData.
 */
function findHeroEntryForDisplay(setData, heroSlug) {
    if (!setData || typeof setData !== 'object')
        return null;
    const candidate = setData;
    if (!Array.isArray(candidate.heroes))
        return null;
    for (const entry of candidate.heroes) {
        if (!entry || typeof entry !== 'object')
            continue;
        const heroEntry = entry;
        if (typeof heroEntry.slug !== 'string')
            continue;
        if (heroEntry.slug !== heroSlug)
            continue;
        if (!Array.isArray(heroEntry.cards))
            continue;
        return heroEntry;
    }
    return null;
}
/**
 * Finds villain cards for a group within the named set's villains[].
 *
 * No cross-set fallback — returns [] if the named set is not loaded or
 * the group slug is not present in it.
 */
function findVillainGroupCards(registry, setAbbr, villainGroupSlug) {
    const setData = registry.getSet(setAbbr);
    if (!setData || typeof setData !== 'object')
        return [];
    const candidate = setData;
    if (!Array.isArray(candidate.villains))
        return [];
    for (const group of candidate.villains) {
        if (group.slug === villainGroupSlug && Array.isArray(group.cards)) {
            return group.cards;
        }
    }
    return [];
}
/**
 * Finds a FlatCard matching a villain card by setAbbr first, then group
 * slug, then card slug.
 *
 * Villain FlatCard key format: {setAbbr}-villain-{groupSlug}-{cardSlug}.
 */
function findFlatCardForVillain(allFlatCards, setAbbr, villainGroupSlug, cardSlug) {
    const expectedKey = `${setAbbr}-villain-${villainGroupSlug}-${cardSlug}`;
    for (const card of allFlatCards) {
        if (card.cardType !== 'villain')
            continue;
        if (card.key === expectedKey)
            return card;
    }
    return undefined;
}
/**
 * Finds a henchman group within the named set's henchmen[].
 *
 * Returns null if the named set is not loaded, the group slug is not
 * present, or the entry shape is malformed. No cross-set fallback.
 */
function findHenchmanGroup(registry, setAbbr, henchmanGroupSlug) {
    const setData = registry.getSet(setAbbr);
    if (!setData || typeof setData !== 'object')
        return null;
    const candidate = setData;
    if (!Array.isArray(candidate.henchmen))
        return null;
    for (const entry of candidate.henchmen) {
        if (!entry || typeof entry !== 'object')
            continue;
        const henchmanEntry = entry;
        if (henchmanEntry.slug === henchmanGroupSlug) {
            return henchmanEntry;
        }
    }
    return null;
}
/**
 * Finds ALL mastermind cards (base + tactics) within the named set's
 * masterminds[] for the given slug.
 *
 * Returns every card — base (`tactic !== true`) and tactics
 * (`tactic === true`) — so the builder can project a display entry for
 * each. Tactic entries are required because each defeated tactic lands in
 * the victory pile (a per-card rendered zone) and would otherwise render
 * as the `<unknown>` placeholder. The caller resolves name/imageUrl per
 * card from the FlatCard list (key `${setAbbr}-mastermind-${slug}-${card.slug}`)
 * and cost from each card's vAttack.
 *
 * Returns [] when the named set is not loaded, the slug is not present, or
 * the mastermind has no cards array. No cross-set fallback.
 */
function findMastermindCardsForDisplay(registry, setAbbr, mastermindSlug) {
    const setData = registry.getSet(setAbbr);
    if (!setData || typeof setData !== 'object')
        return [];
    const candidate = setData;
    if (!Array.isArray(candidate.masterminds))
        return [];
    for (const mastermindRaw of candidate.masterminds) {
        if (!mastermindRaw || typeof mastermindRaw !== 'object')
            continue;
        const mastermind = mastermindRaw;
        if (mastermind.slug !== mastermindSlug)
            continue;
        if (!Array.isArray(mastermind.cards))
            continue;
        return mastermind.cards;
    }
    return [];
}
/**
 * Finds a FlatCard by exact key match.
 */
function findFlatCardByKey(allFlatCards, key) {
    for (const flatCard of allFlatCards) {
        if (flatCard.key === key)
            return flatCard;
    }
    return undefined;
}
// ---------------------------------------------------------------------------
// Physical card helpers (D-14102 migration)
// ---------------------------------------------------------------------------
/**
 * Defensively parses raw physicalCards data into typed entries.
 *
 * Mirrors parsePhysicalCards in buildHeroDeck.ts but also validates and
 * extracts the imageUrl field needed by the display data builder.
 * Malformed entries are silently skipped (defense-in-depth).
 */
function parseDisplayDataPhysicalCards(raw) {
    if (!Array.isArray(raw))
        return [];
    const result = [];
    for (const entry of raw) {
        if (!entry || typeof entry !== 'object')
            continue;
        const candidate = entry;
        if (typeof candidate.id !== 'string' || candidate.id.length === 0)
            continue;
        if (typeof candidate.count !== 'number' || !Number.isInteger(candidate.count) || candidate.count < 1)
            continue;
        if (typeof candidate.imageUrl !== 'string')
            continue;
        if (!Array.isArray(candidate.sides) || candidate.sides.length === 0)
            continue;
        let sidesValid = true;
        for (const side of candidate.sides) {
            if (typeof side !== 'string' || side.length === 0) {
                sidesValid = false;
                break;
            }
        }
        if (!sidesValid)
            continue;
        result.push({
            id: candidate.id,
            count: candidate.count,
            imageUrl: candidate.imageUrl,
            sides: candidate.sides,
        });
    }
    return result;
}
/**
 * Finds a hero card entry by slug within the cards array.
 *
 * Used by the physicalCards migration to resolve name and cost for the
 * canonical face slug (sides[0] per D-14101).
 */
function findCardEntryBySlug(cards, slug) {
    for (const cardEntry of cards) {
        if (!cardEntry || typeof cardEntry !== 'object')
            continue;
        if (cardEntry.slug === slug)
            return cardEntry;
    }
    return null;
}
/**
 * Resolves the WP-315 `abilityText` for a hero card entry from its registry
 * `abilities` array.
 *
 * Joins the non-empty printed lines with a single newline, preserving marker
 * annotations verbatim. Returns undefined (field omitted, never an empty string)
 * when the entry is null, `abilities` is absent/not-an-array, or every line is
 * empty — fail-soft so a card with no printed ability keeps a clean display entry.
 *
 * @param cardEntry - The hero card entry, or null when no match was found.
 * @returns The joined ability text, or undefined when there is none.
 */
function resolveHeroAbilityText(cardEntry) {
    // why: WP-315 — markers (`[keyword:…]`, `[hc:…]`) are kept verbatim; the
    // diagnostic reads printed-text-plus-annotation to check effect-vs-text.
    if (cardEntry === null || !Array.isArray(cardEntry.abilities)) {
        return undefined;
    }
    const lines = [];
    for (const line of cardEntry.abilities) {
        if (typeof line === 'string' && line.length > 0) {
            lines.push(line);
        }
    }
    if (lines.length === 0) {
        return undefined;
    }
    return lines.join('\n');
}
/**
 * Finds the first entry in `setData.other[]` whose `cardType` field
 * matches the supplied value AND carries well-formed string `name` and
 * `imageUrl` fields.
 *
 * Used by section 5 (`cardType === 'mastermind-strike'`) and section 6
 * (`cardType === 'scheme-twist'`) under the D-17201 tiered-lookup
 * pattern (tier-1 source set; tier-2 `core` set; tier-3 literal
 * fallback handled by caller).
 *
 * Returns null when the set is not loaded, `other[]` is absent or not
 * an array, or no well-formed matching entry exists. Malformed entries
 * (null, primitives, missing string fields) are silently skipped per
 * the §C defensive parsing test.
 */
function findOtherEntryByCardType(setData, cardType) {
    if (!setData || typeof setData !== 'object')
        return null;
    const candidate = setData;
    if (!Array.isArray(candidate.other))
        return null;
    for (const entry of candidate.other) {
        if (!entry || typeof entry !== 'object')
            continue;
        const otherEntry = entry;
        if (otherEntry.cardType !== cardType)
            continue;
        if (typeof otherEntry.name !== 'string')
            continue;
        if (typeof otherEntry.imageUrl !== 'string')
            continue;
        return { name: otherEntry.name, imageUrl: otherEntry.imageUrl };
    }
    return null;
}
/**
 * Finds the first entry in `setData.bystanders[]` whose `slug` field
 * equals the canonical generic-bystander literal `'bystander'` AND
 * carries well-formed string `name` and `imageUrl` fields. The
 * slug-match must beat positional `bystanders[0]` because msp1 / vill /
 * wtif / wpnx carry the generic entry mixed with named characters at
 * non-zero positions.
 *
 * Returns null when no slug-match exists; the caller falls through to
 * the tier-2 positional fallback (`findFirstBystanderEntry`).
 */
function findGenericBystanderEntry(setData) {
    if (!setData || typeof setData !== 'object')
        return null;
    const candidate = setData;
    if (!Array.isArray(candidate.bystanders))
        return null;
    for (const entry of candidate.bystanders) {
        if (!entry || typeof entry !== 'object')
            continue;
        const bystanderEntry = entry;
        if (bystanderEntry.slug !== 'bystander')
            continue;
        if (typeof bystanderEntry.name !== 'string')
            continue;
        if (typeof bystanderEntry.imageUrl !== 'string')
            continue;
        return {
            name: bystanderEntry.name,
            imageUrl: bystanderEntry.imageUrl,
        };
    }
    return null;
}
/**
 * Finds the first well-formed entry in `setData.bystanders[]` (tier-2
 * acknowledged-imperfect named-character fallback for section 7).
 *
 * The first entry that carries both string `name` and `imageUrl` is
 * returned. Returns null when `bystanders[]` is absent / empty / all
 * malformed; the caller falls through to the tier-3 literal
 * `{ name: 'Bystander', imageUrl: '' }`.
 */
function findFirstBystanderEntry(setData) {
    if (!setData || typeof setData !== 'object')
        return null;
    const candidate = setData;
    if (!Array.isArray(candidate.bystanders))
        return null;
    for (const entry of candidate.bystanders) {
        if (!entry || typeof entry !== 'object')
            continue;
        const bystanderEntry = entry;
        if (typeof bystanderEntry.name !== 'string')
            continue;
        if (typeof bystanderEntry.imageUrl !== 'string')
            continue;
        return {
            name: bystanderEntry.name,
            imageUrl: bystanderEntry.imageUrl,
        };
    }
    return null;
}
/**
 * Finds a scheme entry within the named set's `schemes[]` by slug.
 *
 * Returns null when the set is not loaded, `schemes[]` is absent or
 * not an array, the slug is not present, or the entry shape is
 * malformed. Optional `villainDeckTwistCount` /
 * `villainDeckBystanderCount` are passed through when present and a
 * `number`; absent fields surface as `undefined` so the caller can
 * apply the engine default.
 */
function findSchemeInSetForDisplay(setData, schemeSlug) {
    if (!setData || typeof setData !== 'object')
        return null;
    const candidate = setData;
    if (!Array.isArray(candidate.schemes))
        return null;
    for (const entry of candidate.schemes) {
        if (!entry || typeof entry !== 'object')
            continue;
        const schemeEntry = entry;
        if (schemeEntry.slug !== schemeSlug)
            continue;
        const result = { slug: schemeEntry.slug };
        if (typeof schemeEntry.villainDeckTwistCount === 'number') {
            result.villainDeckTwistCount = schemeEntry.villainDeckTwistCount;
        }
        if (typeof schemeEntry.villainDeckBystanderCount === 'number') {
            result.villainDeckBystanderCount = schemeEntry.villainDeckBystanderCount;
        }
        return result;
    }
    return null;
}
/**
 * Finds a hero in `setData.heroes[]` whose `slug` matches exactly and
 * returns the canonical face name + image URL.
 *
 * Name is read from `cards[0].name`; imageUrl is read from
 * `physicalCards[0].imageUrl` (the D-14102 canonical-face source per
 * the existing section-1b walk). All field reads gate with
 * `typeof === 'string'` guards before use.
 *
 * Returns null when the set is not loaded, `heroes[]` is absent or not
 * an array, no slug match exists, or the matching entry's `cards[0]` /
 * `physicalCards[0]` does not carry both required string fields.
 * Malformed entries (null, primitive, wrong-type fields) are silently
 * skipped — defensive read of registry data (unknown shape per
 * ARCHITECTURE.md); only `Game.setup()` may throw.
 *
 * Distinct from `findHeroEntryForDisplay` (the existing section-1b
 * helper, which returns the whole `DisplayDataHeroEntry` for the
 * per-copy physicalCards walk). This helper returns only `name` +
 * `imageUrl` for the Section 8 well-known ext_id emission — different
 * call site, different return shape, kept separate per Rule §16.1.
 *
 * @param setData - Per-set data object from the registry (unknown shape).
 * @param heroSlug - The hero slug to match exactly (`'agent'` /
 *   `'trooper'` / `'officer'` for the WP-173 well-known SHIELD ext_ids).
 * @returns Hero display payload, or null on lookup / parse failure.
 */
function findHeroByExactSlug(setData, heroSlug) {
    if (!setData || typeof setData !== 'object')
        return null;
    const candidate = setData;
    if (!Array.isArray(candidate.heroes))
        return null;
    for (const entry of candidate.heroes) {
        if (!entry || typeof entry !== 'object')
            continue;
        const heroEntry = entry;
        if (heroEntry.slug !== heroSlug)
            continue;
        if (!Array.isArray(heroEntry.cards))
            continue;
        const firstCard = heroEntry.cards[0];
        if (!firstCard || typeof firstCard !== 'object')
            continue;
        const cardCandidate = firstCard;
        if (typeof cardCandidate.name !== 'string')
            continue;
        if (!Array.isArray(heroEntry.physicalCards))
            continue;
        const firstPhysical = heroEntry.physicalCards[0];
        if (!firstPhysical || typeof firstPhysical !== 'object')
            continue;
        const physicalCandidate = firstPhysical;
        if (typeof physicalCandidate.imageUrl !== 'string')
            continue;
        return {
            name: cardCandidate.name,
            imageUrl: physicalCandidate.imageUrl,
        };
    }
    return null;
}
/**
 * Finds the first entry in `setData.bystanders[]` whose `slug` field
 * matches the supplied value exactly AND carries well-formed string
 * `name` and `imageUrl` fields.
 *
 * Used by Section 8 for `pile-bystander` (target slug `'bystander'`).
 *
 * Returns null when the set is not loaded, `bystanders[]` is absent or
 * not an array, no slug match exists, or the matching entry lacks the
 * required string fields. Malformed entries are silently skipped —
 * defensive read of registry data (unknown shape per ARCHITECTURE.md);
 * only `Game.setup()` may throw.
 *
 * Kept distinct from the WP-172 `findGenericBystanderEntry` (which
 * hard-codes `slug === 'bystander'`) per Rule §16.1 — two-call-site
 * duplication is preferred over a parameterized merge that would
 * obscure the section-7 vs section-8 call sites' different fallback
 * semantics. Section 7's bystander resolution has a tier-2 positional
 * fallback (`findFirstBystanderEntry`) reflecting per-scheme bystander
 * identity; Section 8's `pile-bystander` is the generic supply-pile
 * token and falls back to a tier-2 literal only.
 *
 * @param setData - Per-set data object from the registry (unknown shape).
 * @param targetSlug - The bystander slug to match exactly.
 * @returns Bystander display payload, or null on lookup / parse failure.
 */
function findBystanderArrayEntry(setData, targetSlug) {
    if (!setData || typeof setData !== 'object')
        return null;
    const candidate = setData;
    if (!Array.isArray(candidate.bystanders))
        return null;
    for (const entry of candidate.bystanders) {
        if (!entry || typeof entry !== 'object')
            continue;
        const bystanderEntry = entry;
        if (bystanderEntry.slug !== targetSlug)
            continue;
        if (typeof bystanderEntry.name !== 'string')
            continue;
        if (typeof bystanderEntry.imageUrl !== 'string')
            continue;
        return {
            name: bystanderEntry.name,
            imageUrl: bystanderEntry.imageUrl,
        };
    }
    return null;
}
/**
 * Finds the first entry in `setData.wounds[]` whose `slug` field equals
 * the canonical wound literal `'wound'` AND carries well-formed string
 * `name` and `imageUrl` fields.
 *
 * Single call site (Section 8 / `pile-wound`); the literal `'wound'` is
 * inlined per Rule §16.1 — parameterization would imply other wound
 * slugs exist, which they do not in any of the 40 registered sets as
 * of 2026-05-23.
 *
 * Returns null when the set is not loaded, `wounds[]` is absent or not
 * an array, no slug match exists, or the matching entry lacks the
 * required string fields. Malformed entries are silently skipped —
 * defensive read of registry data (unknown shape per ARCHITECTURE.md);
 * only `Game.setup()` may throw.
 *
 * @param setData - Per-set data object from the registry (unknown shape).
 * @returns Wound display payload, or null on lookup / parse failure.
 */
function findWoundArrayEntry(setData) {
    if (!setData || typeof setData !== 'object')
        return null;
    const candidate = setData;
    if (!Array.isArray(candidate.wounds))
        return null;
    for (const entry of candidate.wounds) {
        if (!entry || typeof entry !== 'object')
            continue;
        const woundEntry = entry;
        if (woundEntry.slug !== 'wound')
            continue;
        if (typeof woundEntry.name !== 'string')
            continue;
        if (typeof woundEntry.imageUrl !== 'string')
            continue;
        return {
            name: woundEntry.name,
            imageUrl: woundEntry.imageUrl,
        };
    }
    return null;
}
// ---------------------------------------------------------------------------
// Killbot display names (D-24668)
// ---------------------------------------------------------------------------
/** The display name of a villain-deck Bystander converted to a Killbot Villain. */
export const KILLBOT_DISPLAY_NAME = 'Killbot';
/**
 * Renames every Killbot-converted villain-deck Bystander to "Killbot" in the setup-time
 * display data (D-24668).
 *
 * // why: under Replace Earth's Leaders with Killbots the villain-deck Bystanders are
 * Killbot Villains (D-24324), but their display entries kept the Bystander name, so the
 * log, the City card and the Victory Pile read "fought Bystander" and "Villain Bystander
 * escaped" (match zE7zTyM-ziJ). Only 'killbot'-origin entries change; Secret Invasion's
 * Skrull Heroes keep their Hero names, and every other scheme's display data is untouched.
 *
 * @param cardDisplayData - The freshly built display data; mutated in place.
 * @param convertedOrigins - The converted-villain overlay from buildVillainDeck.
 */
export function applyKillbotDisplayNames(cardDisplayData, convertedOrigins) {
    for (const convertedId of Object.keys(convertedOrigins)) {
        if (convertedOrigins[convertedId] !== 'killbot') {
            continue;
        }
        const entry = cardDisplayData[convertedId];
        if (entry === undefined) {
            continue;
        }
        // why: a fresh object per key, so no display entry is aliased.
        cardDisplayData[convertedId] = { ...entry, name: KILLBOT_DISPLAY_NAME };
    }
}
//# sourceMappingURL=buildCardDisplayData.js.map